import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Ban, CalendarClock, Play, RotateCcw } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { DevActorSwitch } from '@/components/patching/DevActorSwitch'
import {
  patchingKeys,
  useApproveStage,
  useCancelSchedule,
  useCancelStage,
  useExecuteStage,
  usePlanStage,
  useRejectStage,
  useScheduleStage,
} from '@/hooks/usePatching'
import { patchingApi, toPatchApiError } from '@/services/patching'
import { formatDateTime } from '@/utils/format'
import { approvedWindow, getStageActionGates, isRunningStatus, sameUser } from '@/utils/patchStage'
import type { ApproveStageBody, PatchCampaign, PatchMe, PatchScheduleRow, PatchStage } from '@/types/patching'
import { ApprovalForm, type ActionResult } from './ApprovalForm'
import styles from './Patching.module.css'

interface StageActionsProps {
  campaign: PatchCampaign
  stage: PatchStage
  me: PatchMe | undefined
  meLoading: boolean
  meError: string | null
}

const REFRESHED_HINT = 'The campaign was refreshed. Review the current plan and scope, then approve again if it is still right.'

/**
 * Stage action buttons. Visibility follows `GET me/` and the stage status, but this is a
 * convenience only: the portal decides, and its 403/409/400 messages are shown verbatim.
 */
export function StageActions({ campaign, stage, me, meLoading, meError }: StageActionsProps) {
  const env = stage.environment
  const campaignId = campaign.id
  const gates = getStageActionGates(me, campaign, stage)

  const plan = usePlanStage()
  const approve = useApproveStage()
  const reject = useRejectStage()
  const execute = useExecuteStage()
  const schedule = useScheduleStage()
  const cancelSchedule = useCancelSchedule()
  const cancel = useCancelStage()
  const busy =
    plan.isPending ||
    approve.isPending ||
    reject.isPending ||
    execute.isPending ||
    schedule.isPending ||
    cancelSchedule.isPending ||
    cancel.isPending

  const [dryRun, setDryRun] = useState(false)
  const [windowStart, setWindowStart] = useState('')
  const [windowEnd, setWindowEnd] = useState('')
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')
  const [idempotencyKey] = useState(() => `sched-${Date.now()}`)
  const [scheduled, setScheduled] = useState<PatchScheduleRow | null>(null)
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)

  const selfRequested = !!me && sameUser(me.email, campaign.requested_by)
  const approved = approvedWindow(stage)

  async function run(action: () => Promise<unknown>): Promise<ActionResult> {
    setMessage(null)
    setHint(null)
    try {
      await action()
      return { ok: true, status: null }
    } catch (err) {
      const error = toPatchApiError(err)
      setMessage(error.message)
      return { ok: false, status: error.status }
    }
  }

  const onPlan = () => run(() => plan.mutateAsync({ campaignId, env }))

  async function onApprove(body: ApproveStageBody): Promise<ActionResult> {
    const result = await run(() => approve.mutateAsync({ campaignId, env, body }))
    if (result.status === 409) setHint(REFRESHED_HINT)
    return result
  }

  const onReject = (notes: string) => run(() => reject.mutateAsync({ campaignId, env, body: { notes } }))

  // Execute takes only { dry_run } — the maintenance window was fixed by the approver.
  const onExecute = () => run(() => execute.mutateAsync({ campaignId, env, body: { dry_run: dryRun } }))

  async function onSchedule() {
    setMessage(null)
    setHint(null)
    try {
      const row = await schedule.mutateAsync({
        campaignId,
        env,
        body: { start: windowStart, end: windowEnd, timezone, idempotency_key: idempotencyKey },
      })
      setScheduled(row)
    } catch (err) {
      setMessage(toPatchApiError(err).message)
    }
  }

  async function onCancelSchedule() {
    if (!scheduled) return
    setMessage(null)
    try {
      setScheduled(await cancelSchedule.mutateAsync({ campaignId, env, scheduleId: scheduled.id }))
    } catch (err) {
      setMessage(toPatchApiError(err).message)
    }
  }

  async function onCancel() {
    const result = await run(() => cancel.mutateAsync({ campaignId, env }))
    if (result.ok) setConfirmingCancel(false)
  }

  const anyVisible = Object.values(gates).some((g) => g.visible)
  const planLabel = stage.status === 'failed' ? 'Retry plan' : stage.status === 'cancelled' ? 'Re-plan' : 'Plan'
  const waitingForApproval = stage.status === 'awaiting_approval' && !gates.approve.visible && !meLoading
  const assignments = useQuery({
    queryKey: [...patchingKeys.all, 'assignments'],
    queryFn: () => patchingApi.listApproverAssignments(),
    enabled: waitingForApproval && !!me?.is_patch_admin,
    retry: false,
  })
  const approverEmails = (assignments.data ?? [])
    .filter((row) => row.environment === env && row.active !== false)
    .map((row) => row.user_email)

  return (
    <div data-testid="stage-actions">
      {meLoading && <p className={styles.muted}>Checking your permissions…</p>}
      {meError && (
        <div className={clsx(styles.alert, styles.alertWarning)} data-testid="me-error">
          <AlertTriangle size={16} className={styles.alertIcon} />
          <span>Your permissions could not be loaded, so actions are hidden. {meError}</span>
        </div>
      )}
      {waitingForApproval && (
        <div className={clsx(styles.alert, styles.alertWarning)} data-testid="approval-waiting">
          <AlertTriangle size={16} className={styles.alertIcon} />
          <div>
            <p>
              This {env.toUpperCase()} plan is waiting for approval. Installation starts only after an assigned approver approves it.
              {me?.email ? ` You are signed in as ${me.email}.` : ''}
              {selfRequested ? ' You requested this campaign, so this sign-in cannot approve it.' : ''}
            </p>
            {approverEmails.length > 0 && <p>Assigned {env.toUpperCase()} approvers: {approverEmails.join(', ')}.</p>}
            {import.meta.env.VITE_DEV_ACTOR_SWITCH === 'true' && (
              <p>Choose DEV approver below, then use Approve.</p>
            )}
            <DevActorSwitch />
          </div>
        </div>
      )}

      {!meLoading && !meError && !anyVisible && (
        <p className={styles.muted} data-testid="no-actions">
          {isRunningStatus(stage.status)
            ? 'This stage is running. The page refreshes automatically.'
            : 'No actions are available to you at this stage.'}
        </p>
      )}

      {gates.plan.visible && (
        <div className={styles.actionBlock}>
          <div className={styles.actionRow}>
            <Button
              icon={stage.status === 'not_started' ? <Play size={14} /> : <RotateCcw size={14} />}
              onClick={onPlan}
              loading={plan.isPending}
              disabled={busy || gates.plan.disabledReason !== null}
              title={gates.plan.disabledReason ?? undefined}
            >
              {planLabel}
            </Button>
          </div>
          {gates.plan.disabledReason && <span className={styles.hint}>{gates.plan.disabledReason}</span>}
          {(stage.status === 'failed' || stage.status === 'cancelled') && (
            <span className={styles.hint}>
              {stage.status === 'failed' ? 'Retrying' : 'Re-planning'} starts a new attempt for this environment; any earlier
              approval is void.
            </span>
          )}
        </div>
      )}

      {(gates.approve.visible || gates.reject.visible) && (
        <ApprovalForm
          campaign={campaign}
          stage={stage}
          approve={gates.approve}
          reject={gates.reject}
          busy={busy}
          approving={approve.isPending}
          rejecting={reject.isPending}
          selfRequested={selfRequested}
          onApprove={onApprove}
          onReject={onReject}
        />
      )}

      {gates.execute.visible && (
        <div className={styles.actionBlock}>
          {approved ? (
            <div className={styles.field} data-testid="approved-window">
              <span className={styles.label}>Approved maintenance window</span>
              <span className={styles.factValue}>
                {formatDateTime(approved.start)} – {formatDateTime(approved.end)} (your local time)
              </span>
              <span className={styles.hint}>Set by the approver. Execution is only allowed inside this window.</span>
            </div>
          ) : (
            env === 'prod' && (
              <span className={styles.hint} data-testid="no-approved-window">
                No approved maintenance window is recorded for PROD; the portal will refuse to dispatch.
              </span>
            )
          )}
          <label className={styles.checkboxRow}>
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="dry-run-toggle" />
            <span>Dry run — run pre-flight and assessment only; nothing is installed.</span>
          </label>
          {stage.dry_run && stage.status === 'approved' && (
            <span className={styles.hint} data-testid="execute-live-hint">
              The last run was a dry run, so nothing was installed.
              {env === 'dev' ? ' Leave Dry run unchecked and choose Execute to install patches.' : ''}
            </span>
          )}
          <div className={styles.actionRow}>
            <Button
              icon={<Play size={14} />}
              onClick={onExecute}
              loading={execute.isPending}
              disabled={busy || gates.execute.disabledReason !== null}
              title={gates.execute.disabledReason ?? undefined}
            >
              {dryRun ? 'Execute (dry run)' : 'Execute'}
            </Button>
          </div>
          {gates.execute.disabledReason && <span className={styles.hint}>{gates.execute.disabledReason}</span>}
        </div>
      )}

      {gates.schedule.visible && (
        <div className={styles.actionBlock} data-testid="schedule-panel">
          <span className={styles.hint}>
            Schedule stores the window on the server and dispatches execution when it opens. Cancelling a schedule that has not started does not reverse a patch that is already running.
          </span>
          <label className={styles.field}>
            <span className={styles.label}>Start</span>
            <input className={styles.input} type="datetime-local" value={windowStart} onChange={(event) => setWindowStart(event.target.value)} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>End</span>
            <input className={styles.input} type="datetime-local" value={windowEnd} onChange={(event) => setWindowEnd(event.target.value)} />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Timezone</span>
            <input className={styles.input} value={timezone} onChange={(event) => setTimezone(event.target.value)} />
          </label>
          <div className={styles.actionRow}>
            <Button
              icon={<CalendarClock size={14} />}
              onClick={onSchedule}
              loading={schedule.isPending}
              disabled={busy || gates.schedule.disabledReason !== null || !windowStart || !windowEnd}
            >
              Schedule
            </Button>
            {scheduled?.status === 'scheduled' && (
              <Button variant="secondary" onClick={onCancelSchedule} loading={cancelSchedule.isPending} disabled={busy}>
                Cancel schedule
              </Button>
            )}
          </div>
          {scheduled && (
            <span className={styles.hint} data-testid="schedule-status">
              Schedule {scheduled.id} is {scheduled.status}. {scheduled.detail?.note || scheduled.detail?.error || ''}
            </span>
          )}
          {gates.schedule.disabledReason && <span className={styles.hint}>{gates.schedule.disabledReason}</span>}
        </div>
      )}

      {gates.cancel.visible && (
        <div className={styles.actionBlock}>
          {confirmingCancel ? (
            <>
              <div className={styles.actionRow}>
                <Button variant="danger" icon={<Ban size={14} />} onClick={onCancel} loading={cancel.isPending} disabled={busy}>
                  Confirm cancel {env.toUpperCase()}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmingCancel(false)} disabled={busy}>
                  Keep
                </Button>
              </div>
              <span className={styles.hint}>
                The portal will also try to cancel the GitHub run. If it cannot confirm that, you will be told the run may still be active.
              </span>
            </>
          ) : (
            <div className={styles.actionRow}>
              <Button variant="secondary" icon={<Ban size={14} />} onClick={() => setConfirmingCancel(true)} disabled={busy}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}

      {message && (
        <div className={clsx(styles.alert, styles.alertError)} role="alert" data-testid="action-error" style={{ marginTop: 12 }}>
          <AlertTriangle size={16} className={styles.alertIcon} />
          <span>{message}</span>
        </div>
      )}
      {hint && (
        <p className={styles.hint} data-testid="action-hint" style={{ marginTop: 6 }}>
          {hint}
        </p>
      )}
    </div>
  )
}
